import { useRBACDataContext } from '../../hooks/rbac/useRBACDataContext';
import { useRBACPermissionsContext } from '../../hooks/rbac/useRBACPermissionsContext';

/**
 * Enhanced RBAC Context Hook - Unified Interface
 * 
 * This hook combines both RBAC data and permissions contexts into a single interface
 * while maintaining the performance benefits of the split context architecture.
 * 
 * Key Features:
 * - Backward compatibility with existing RBAC usage
 * - Performance optimization through split contexts
 * - Unified interface for easier development
 * - Type-safe access to all RBAC functionality
 * 
 * Architecture:
 * - Uses separate data and permissions contexts under the hood
 * - Prevents unnecessary re-renders by isolating data vs permission changes
 * - Provides both grouped and direct access to functions
 * - Maintains consistent API for existing components
 */
export const useEnhancedRBACContext = () => {
  const dataContext = useRBACDataContext();
  const permissionsContext = useRBACPermissionsContext();

  return {
    // Data context values
    ...dataContext,
    
    // Permissions context values
    permissions: {
      userRole: permissionsContext.userRole,
      canView: permissionsContext.canView,
      canEdit: permissionsContext.canEdit,
      canDelete: permissionsContext.canDelete,
      canAccessFeature: permissionsContext.canAccessFeature,
      checkPermissionWithAudit: permissionsContext.checkPermissionWithAudit,
      hasRole: permissionsContext.hasRole,
    },
    
    // Direct access to permission functions for backward compatibility
    userRole: permissionsContext.userRole,
    canView: permissionsContext.canView,
    canEdit: permissionsContext.canEdit,
    canDelete: permissionsContext.canDelete,
    canAccessFeature: permissionsContext.canAccessFeature,
    checkPermissionWithAudit: permissionsContext.checkPermissionWithAudit,
    hasRole: permissionsContext.hasRole,
    isLoading: permissionsContext.isLoading,
    error: permissionsContext.error,
  };
};